import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import Button from '@/components/Button';
import DeliveryCards from '@/components/Cards/DeliveryCards/DeliveryCards';
import { getEtaDays } from '@/utils/common';

import GeosuggestInput from './dependencies/geosuggest';
import useCoverage from './useCoverage';

const CoverageSection = () => {
  const theme = useTheme();
  const hook = useCoverage();
  const navigate = useNavigate();

  const { t } = useTranslation('coverage');
  return (
    <Paper
      elevation={3}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '13.3125rem',
        padding: `${theme.spacing(4)}`,
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="2rem"
        width="100%"
        mt="1rem"
      >
        <GeosuggestInput
          label={
            <Typography
              variant="subtitle2"
              fontWeight={theme.typography.fontWeightMedium}
              color="secondary"
            >
              ¿Dónde tenemos que{' '}
              <span style={{ color: theme.palette.primary.main }}>recolectar</span> el paquete?
            </Typography>
          }
          placeholder={`Ingresa tu dirección de origen (${hook.postalCodePlaceholder})`}
          name="origin"
          onChange={hook.handleAddress}
        />

        {hook.shouldShowManualInput('origin') && (
          <Box sx={{ width: '100%', mt: -1 }}>
            {hook.address.origin?.codePostal === 'MISSING' && (
              <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                ⚠️ {t('missingOriginPostalCode')}
              </Typography>
            )}
            <TextField
              fullWidth
              label={t('enterPostalCodeManually', 'Código postal de origen')}
              placeholder={hook.postalCodePlaceholder}
              value={hook.getManualPostalCodeValue('origin')}
              onChange={(e) => hook.handleManualPostalCode('origin', e.target.value)}
              inputProps={{
                maxLength: hook.getPostalCodeMaxLength(),
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </Box>
        )}

        <GeosuggestInput
          label={
            <Typography
              variant="subtitle2"
              fontWeight={theme.typography.fontWeightMedium}
              color="secondary"
            >
              ¿Dónde debemos de <span style={{ color: theme.palette.primary.main }}>entregar</span>{' '}
              el paquete?
            </Typography>
          }
          placeholder={`Ingresa tu dirección de destino (${hook.postalCodePlaceholder})`}
          name="destination"
          onChange={hook.handleAddress}
        />

        {hook.shouldShowManualInput('destination') && (
          <Box sx={{ width: '100%', mt: -1 }}>
            {hook.address.destination?.codePostal === 'MISSING' && (
              <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                ⚠️ {t('missingDestinationPostalCode')}
              </Typography>
            )}
            <TextField
              fullWidth
              label={t('enterDestinationPostalCodeManually', 'Código postal de destino')}
              placeholder={hook.postalCodePlaceholder}
              value={hook.getManualPostalCodeValue('destination')}
              onChange={(e) => hook.handleManualPostalCode('destination', e.target.value)}
              inputProps={{
                maxLength: hook.getPostalCodeMaxLength(),
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>
      <Button
        variant="contained"
        size="medium"
        onClick={hook.getQuotation}
        disabled={hook.disableButton}
        sx={{ mt: '1rem', alignSelf: 'end' }}
      >
        {hook.quot.loading ? <CircularProgress sx={{ color: 'white' }} size={20} /> : 'Verificar'}
      </Button>
      {hook.quot.error && (
        <Typography variant="body2" color="error" mt="1rem">
          Lo sentimos, no contamos con cobertura en las direcciones seleccionadas.
        </Typography>
      )}
      {hook.quot.data && hook.quot.data.length > 0 && (
        <Box display="flex" flexDirection="column" maxWidth="fit-content" margin="0 auto">
          <Typography
            variant="subtitle2"
            color={theme.palette.secondary.main}
            fontWeight={theme.typography.fontWeightMedium}
          >
            {t('availableServices')}
          </Typography>
          <Grid container spacing={2} mt={2} sx={{ display: 'flex', justifyContent: 'center' }}>
            {hook.quot.data?.map((item) => (
              <DeliveryCards
                key={item.deliveryTypeCode}
                deliveryType={item.deliveryType}
                time={getEtaDays(item.eta.addedDays)}
                price={`${t('currencySymbol')} ${item.prices.totalWithoutCOD} ${t('currencyNomenclature')}*`}
                Icon={hook.handleDeliveryIcon(item.deliveryTypeCode)}
              />
            ))}
          </Grid>
          <Typography variant="caption" mt="1rem">
            {t('coverageAdvice')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{ mt: '1rem', alignSelf: 'center' }}
          >
            Crear pedido
          </Button>
        </Box>
      )}
      {hook.country === 'MEX' && (
        <Typography
          variant="body2"
          mt="1rem"
          textAlign="center"
          color="secondary"
          fontWeight={theme.typography.fontWeightMedium}
        >
          Haz clic aquí para descargar{' '}
          <a
            href="https://99downloads.s3.us-west-1.amazonaws.com/Cobertura_99minutos.xlsx"
            target="_blank"
            style={{ cursor: 'pointer', color: theme.palette.primary.main }}
          >
            todas
          </a>{' '}
          nuestras coberturas
        </Typography>
      )}
    </Paper>
  );
};

export default CoverageSection;
